import { useCallback, useEffect, useState } from "react";
import { CONFIG } from "../config.js";
import { toast } from "../toast.ts";
import type { AdminRow, EntryApi } from "../domain.ts";

// Màn Admin: 3 bảng entry gần nhất + nút xoá từng dòng, picker active tech
// stack, và checklist vị trí còn thiếu log trong 7 ngày.
//
// Data ở đây là dòng THÔ của SharePoint (cần Id để xoá, Created để hiện), nên
// mọi field đọc qua helper ép kiểu — xem AdminRow trong domain.ts.

const txt = (r: AdminRow, key: string): string => {
  const v = r[key];
  return v == null ? "" : String(v);
};
const day = (r: AdminRow, key: string): string => txt(r, key).slice(0, 10);

type Data = { funnel: AdminRow[]; kpi: AdminRow[]; rto: AdminRow[] };

export type AdminPanelProps = {
  api: EntryApi;
  isDemo: boolean;
  activeStacks: readonly string[];
  onSaveStacks: (stacks: string[]) => Promise<void>;
  /** Nạp lại data dashboard sau khi xoá 1 dòng. */
  onDataChanged: () => Promise<void>;
  onClose: () => void;
};

export function AdminPanel({
  api, isDemo, activeStacks, onSaveStacks, onDataChanged, onClose,
}: AdminPanelProps) {
  const [data, setData] = useState<Data | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[]>([...activeStacks]);
  const [savingStacks, setSavingStacks] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await api.getRecentEntries();
      setData({ funnel: d.funnel, kpi: d.kpi, rto: d.rto ?? [] });
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    }
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  if (loadError) {
    return (
      <section className="card" id="admin-section">
        <div className="kpi-empty">Không tải được entries: {loadError}</div>
      </section>
    );
  }

  const head = (
    <div className="card-head">
      <h2>🛠 Admin — Recent Entries</h2>
      <button className="btn-plain" id="admin-close" onClick={onClose}>Close</button>
    </div>
  );

  if (!data) {
    return (
      <section className="card" id="admin-section">
        <div className="card-head">
          <h2>🛠 Admin — Recent Entries</h2>
          <span className="card-sub">LOADING…</span>
        </div>
      </section>
    );
  }

  const { funnel, kpi, rto } = data;

  // Log theo NGÀY → checklist "còn thiếu" xét cửa sổ 7 ngày gần nhất (tính từ
  // ngày log mới nhất), không xét đúng 1 ngày.
  const latestDay = funnel.reduce((m, r) => (day(r, "WeekEnding") > m ? day(r, "WeekEnding") : m), "");
  const cutoff = latestDay
    ? new Date(new Date(`${latestDay}T12:00:00Z`).getTime() - 6 * 86400000).toISOString().slice(0, 10)
    : "";
  const logged = new Set(
    funnel.filter((r) => day(r, "WeekEnding") >= cutoff).map((r) => txt(r, "Position")));
  const missing = CONFIG.positions.filter((p) => !logged.has(p));

  const remove = async (listKey: "funnel" | "kpi" | "rto", id: number) => {
    if (!confirm("Xóa dòng này? (không hoàn tác được)")) return;
    const key = `${listKey}-${id}`;
    setDeleting(key);
    try {
      const listTitle = { funnel: CONFIG.funnelList, kpi: CONFIG.kpiList, rto: CONFIG.rtoList }[listKey];
      await api.deleteItem(listTitle, id);
      toast(isDemo ? "DEMO — đã xóa khỏi bộ nhớ." : "✓ Đã xóa.");
      await onDataChanged();
      await load(); // vẽ lại bảng — bản cũ phải đóng/mở lại màn Admin
    } catch (e) {
      toast(`Không xóa được: ${e instanceof Error ? e.message : String(e)}`, false);
    } finally {
      setDeleting(null);
    }
  };

  const delCell = (listKey: "funnel" | "kpi" | "rto", id: number) => (
    <td>
      <button
        className="btn-del" data-list={listKey} data-id={id}
        disabled={deleting === `${listKey}-${id}`}
        onClick={() => void remove(listKey, id)}
      >🗑</button>
    </td>
  );

  return (
    <section className="card" id="admin-section">
      {head}

      <h3 className="admin-h3">
        Active tech stacks — benchmark Ready to Offer ({CONFIG.rtoTargetMin}–{CONFIG.rtoTargetMax}/stack)
      </h3>
      <div className="stack-picker" id="stack-picker">
        {CONFIG.positions.map((p) => (
          <label className="stack-chip" key={p}>
            <input
              type="checkbox" value={p} checked={picked.includes(p)}
              onChange={(e) => setPicked((prev) =>
                e.target.checked ? [...prev, p] : prev.filter((x) => x !== p))}
            />
            <span>{p}</span>
          </label>
        ))}
        <button
          className="btn-primary" id="stacks-save" disabled={savingStacks}
          onClick={async () => {
            setSavingStacks(true);
            try {
              await onSaveStacks(picked);
              toast(isDemo ? "DEMO — đã áp dụng (chưa lưu thật)." : "✓ Đã lưu active stacks.");
            } catch (e) {
              toast(`Không lưu được: ${e instanceof Error ? e.message : String(e)}`, false);
            } finally {
              setSavingStacks(false);
            }
          }}
        >
          {savingStacks ? "Saving…" : "Save stacks"}
        </button>
      </div>

      <p className="kpi-note">
        7 ngày gần nhất ({cutoff || "—"} → <b>{latestDay || "—"}</b>) còn thiếu:{" "}
        {missing.length
          ? missing.map((p) => <span className="chip" key={p}>{p}</span>)
          : <b>đủ {CONFIG.positions.length} vị trí ✓</b>}
      </p>

      <h3 className="admin-h3">Funnel entries ({funnel.length})</h3>
      <div className="admin-scroll">
        <table className="lb-table admin-table">
          <thead>
            <tr><th>Date</th><th>Position</th><th>C/R/A/I/O/H</th><th>Notes</th><th>By</th><th>Created</th><th /></tr>
          </thead>
          <tbody>
            {funnel.map((r) => (
              <tr key={r.Id}>
                <td>{day(r, "WeekEnding")}</td>
                <td>{txt(r, "Position")}</td>
                <td>
                  {txt(r, "CandidatesContacted")}/{txt(r, "CandidatesResponses")}/
                  {txt(r, "Applications")}/{txt(r, "Interviews")}/
                  {txt(r, "Offers")}/{txt(r, "Hires")}
                </td>
                <td className="admin-notes">{txt(r, "Notes")}</td>
                <td>{r.Author?.Title ?? ""}</td>
                <td>{txt(r, "Created").slice(0, 10)}</td>
                {delCell("funnel", r.Id)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="admin-h3">Ready to Offer entries ({rto.length})</h3>
      <div className="admin-scroll">
        <table className="lb-table admin-table">
          <thead>
            <tr><th>RTO date</th><th>Candidate</th><th>Position</th><th>Level</th><th>Status</th><th>By</th><th /></tr>
          </thead>
          <tbody>
            {rto.map((r) => (
              <tr key={r.Id}>
                <td>{day(r, "RTODate")}</td>
                <td>{txt(r, "Title")}</td>
                <td>{txt(r, "Position")}</td>
                <td>{txt(r, "TechLevel")}</td>
                <td>{txt(r, "RTOStatus")}</td>
                <td>{r.Author?.Title ?? ""}</td>
                {delCell("rto", r.Id)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="admin-h3">KPI achievements ({kpi.length})</h3>
      <div className="admin-scroll">
        <table className="lb-table admin-table">
          <thead>
            <tr><th>Month</th><th>Recruiter</th><th>KPI Type</th><th>Candidate</th><th>By</th><th /></tr>
          </thead>
          <tbody>
            {kpi.map((r) => (
              <tr key={r.Id}>
                <td>{txt(r, "KPIMonth").slice(0, 7)}</td>
                <td>{r.Recruiter?.Title ?? ""}</td>
                <td>{txt(r, "KPIType")}</td>
                <td>{txt(r, "Title")}</td>
                <td>{r.Author?.Title ?? ""}</td>
                {delCell("kpi", r.Id)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
